import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Grid,
  Button,
  Snackbar,
  Alert,
  Paper,
  Chip,
  Divider,
  Stack,
  useMediaQuery,
  Tabs,
  Tab,
} from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useTheme } from "@mui/material/styles";
import "./EntrevistaViewer.css";
import { useEntrevistaData, SECTION_IDS, parseDate } from "../hooks/useEntrevistaData";
import { updatePaciente, updateEntrevista, deleteEntrevista } from "../api/entrevistaApi";
import { isAdminSession } from "../../../shared/auth/session";
import EntrevistaView from "./EntrevistaView";
import EntrevistaEdit from "./EntrevistaEdit";
import PermissionGate from '../../../shared/auth/PermissionGate';

const EntrevistaViewer = ({ idEntrevista, paciente }) => {
  const navigate = useNavigate();
  const state = useEntrevistaData(idEntrevista, paciente);
  const [isEditing, setIsEditing] = useState(false);
  const [openGood, setOpenGood] = useState(false);
  const [openBad, setOpenBad] = useState(false);

  useEffect(() => {
    if (state.loadError) setOpenBad(true);
  }, [state.loadError]);
  const isAdmin = useMemo(() => isAdminSession(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeSection, setActiveSection] = useState("datos");
  const sectionRefs = useRef({});

  const sectionCardSx = {
    scrollMarginTop: { xs: 88, md: 170 },
    px: { xs: 2, md: 3 },
    py: { xs: 2.5, md: 3 },
  };

  const handleSectionChange = (_, value) => {
    setActiveSection(value);
    sectionRefs.current[value]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => setIsEditing(false);

  const handleSave = () => {
    const {
      nombre, apellidos, telefono, dateBirth, numViven, numGrupo, numNivelEducacional, numSexo,
      codigoEntrevista, edad, numFumador, tiempoFumador, aniosDeshabilitacion,
      numAlcohol, tiempoAlcohol, aniosDeshabilitacionAlcohol, caidas12Meses, numCaidas,
      ayudaLocomocion, protesisArticulares, protesisRodilla, protesisCadera, protesisHombro,
      protesisManoDedos, protesisOtros, vertigos, mareosAndar, cardiovascular, hipertension,
      insuficienciaCardiaca, accCerebrovascular, anginaPecho, infarto, trombosis, otrosCardiovascular,
      respiratorio, asma, epoc, otrosRespiratorio, genitoUrinario, infeccionUrinaria, calculosRenales,
      insuficienciaRenal, incontinenciaUrinaria, prostata, otrosGenitoUrinario, endocrino, diabetes,
      hipotiroidismo, hipercolesterolemia, osteoporosis, otrosEndocrino, digestivo, herniaHiato,
      reflujo, pancreatitis, enfermedadHepatica, diverticulitis, otrosDigestivo, musculoEsqueletico,
      pieTobillo, rodilla, cadera, lumbar, dorsal, cervical, cabeza, hombro, codo, manoMunieca,
      nervioso, depresion, ansiedad, esclerosisMultiple, parkinson, alzheimer, deterioroCognitivo,
      neuropatiaPeriferica, otrosNervioso, sentidos, hipoacusia, tinitus, perdidaVision,
      datosInteres, medicacion, antihipertensivos, antidiabeticos, estatinas,
      anticoagulantes, tiroides, diuretico, analgesicos, antidepresivos, ansioliticos,
      antiosteoporoticos, inhalador, protectorEstomago, otrosMedicacion,
      numAnalgesicos,
      tensionPAS, tensionPAD, tensionPMM, actividadesSeleccionadas,
      caidasFinales, numPastillas, numAnalgesicosFinal, cambioMedicacionFinal,
      consultaMedico, ingresosHospital, datosInteresFinal,
    } = state;

    updatePaciente({
      pacienteId: paciente,
      payload: {
        firstname: nombre,
        lastname: apellidos,
        telefono,
        dateBirth: parseDate(dateBirth),
        viven: numViven,
        grupo: numGrupo,
        nivelEducacional: numNivelEducacional,
        sexo: numSexo,
      },
    })
      .then(() =>
        updateEntrevista({
          codigoEntrevista,
          payload: {
            edad,
            fumador: numFumador,
            tiempoFumador: parseInt(tiempoFumador, 10) || null,
            aniosDeshabilitacion: parseInt(aniosDeshabilitacion, 10) || null,
            alcohol: numAlcohol,
            tiempoAlcohol: parseInt(tiempoAlcohol, 10) || null,
            aniosDeshabilitacionAlcohol: parseInt(aniosDeshabilitacionAlcohol, 10) || null,
            caidas12Meses,
            numCaidas: parseInt(numCaidas, 10) || 0,
            ayudaLocomocion,
            protesisArticulares,
            protesisRodilla,
            protesisCadera,
            protesisHombro,
            protesisManoDedos,
            protesisOtros,
            vertigos,
            mareosAndar,
            cardiovascular,
            hipertension,
            insuficienciaCardiaca,
            accCerebrovascular,
            anginaPecho,
            infarto,
            trombosis,
            otrosCardiovascular,
            respiratorio,
            asma,
            epoc,
            otrosRespiratorio: otrosRespiratorio || "",
            genitoUrinario,
            infeccionUrinaria,
            calculosRenales,
            insuficienciaRenal,
            incontinenciaUrinaria,
            prostata,
            otrosGenitoUrinario,
            endocrino,
            diabetes,
            hipotiroidismo,
            hipercolesterolemia,
            osteoporosis,
            otrosEndocrino,
            digestivo,
            herniaHiato,
            reflujo,
            pancreatitis,
            enfermedadHepatica,
            diverticulitis,
            otrosDigestivo,
            musculoEsqueletico,
            pieTobillo,
            rodilla,
            cadera,
            lumbar,
            dorsal,
            cervical,
            cabeza,
            hombro,
            codo,
            mano_munieca: manoMunieca,
            nervioso,
            depresion,
            ansiedad,
            esclerosisMultiple,
            parkinson,
            alzheimer,
            deterioroCognitivo,
            neuropatiaPeriferica,
            otrosNervioso,
            sentidos,
            hipoacusia,
            tinitus,
            perdidaVision,
            datosInteres,
            medicacion,
            antihipertensivos,
            antidiabeticos,
            estatinas,
            anticoagulantes,
            tiroides,
            diuretico,
            analgesicos,
            numAnalgesicos: parseInt(numAnalgesicos, 10) || 0,
            antidepresivos,
            ansioliticos,
            antiosteoporoticos,
            inhalador,
            protectorEstomago,
            otrosMedicacion,
            tensionPAS: parseInt(tensionPAS, 10) || 0,
            tensionPAD: parseInt(tensionPAD, 10) || 0,
            tensionPMM: parseFloat(tensionPMM) || 0,
            actividades: actividadesSeleccionadas.join(", "),
            caidasFinales: parseInt(caidasFinales, 10) || 0,
            numPastillas: parseInt(numPastillas, 10) || 0,
            numAnalgesicosFinal: parseInt(numAnalgesicosFinal, 10) || 0,
            cambioMedicacionFinal,
            consultaMedico,
            ingresosHospital,
            datosInteresFinal,
          },
        })
      )
      .then(() => {
        setIsEditing(false);
        setOpenGood(true);
      })
      .catch((err) => {
        console.error(err);
        setOpenBad(true);
      });
  };

  const handleDelete = () => {
    if (!isAdmin) return;
    if (!window.confirm("¿Seguro de eliminar este informe?")) return;
    deleteEntrevista(state.codigoEntrevista)
      .then(() => { navigate("/home"); })
      .catch(console.error);
  };

  const exportPDF = () => {
    setIsEditing(false);
    const ids = ["pdf-content", "pdf-content-2", "pdf-content-3"];
    const pdf = new jsPDF();
    const addPage = (i) => {
      const el = document.getElementById(ids[i]);
      return html2canvas(el, { scale: 2, backgroundColor: null, useCORS: true }).then((canvas) => {
        const img = canvas.toDataURL("image/png");
        const w = pdf.internal.pageSize.getWidth();
        const h = (canvas.height * w) / canvas.width;
        pdf.addImage(img, "PNG", 0, 0, w, h);
        if (i < ids.length - 1) { pdf.addPage(); return addPage(i + 1); }
      });
    };
    addPage(0)
      .then(() => pdf.save(`entrevista_${state.nombre}_${state.apellidos}_${state.createdAt}.pdf`))
      .catch(console.error);
  };

  useEffect(() => {
    const observers = [];
    if (typeof IntersectionObserver === "undefined") return undefined;

    SECTION_IDS.forEach((sectionId) => {
      const element = sectionRefs.current[sectionId];
      if (!element) return;
      const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(sectionId); },
        { rootMargin: "-25% 0px -55% 0px", threshold: 0.1 }
      );
      observer.observe(element);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const { tipoEntrevista, createdAt, nombre, apellidos, codigoEntrevista, numGrupo, numViven } = state;

  return (
    <Box
      className="entrevista-viewer"
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        }}
    >
      <Box className="entrevista-viewer__shell" sx={{ width: "100%" }}>

        {/* Hero */}
        <Paper
          className="entrevista-viewer__hero"
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 4,
            border: "1px solid #dbe7f3",
            background: "linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)",
            boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Box>
                <Typography variant="overline" sx={{ color: "#1d4ed8", letterSpacing: 1.4 }}>
                  Revisión clínica
                </Typography>
                <Typography
                  variant={isMobile ? "h4" : "h3"}
                  sx={{ color: "#0f172a", fontWeight: 600, lineHeight: 1.1, mb: 1 }}
                >
                  Entrevista clínica
                </Typography>
                <Typography sx={{ color: "#475569", maxWidth: 760 }}>
                  Consulta y edita la información clínica en un formato de lectura continua.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap">
                <Chip label={tipoEntrevista || "Sin tipo"} sx={{ backgroundColor: "#dbeafe", color: "#1d4ed8", fontWeight: 600 }} />
                <Chip label={`Fecha ${createdAt || "-"}`} variant="outlined" sx={{ borderColor: "#bfdbfe", color: "#1e3a8a" }} />
                <Chip label={isEditing ? "Modo edición" : "Solo lectura"} color={isEditing ? "warning" : "default"} />
              </Stack>
            </Stack>
            <Divider />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 0.5 }}>Paciente</Typography>
                <Typography sx={{ color: "#0f172a", fontWeight: 600 }}>
                  {nombre || apellidos ? `${nombre} ${apellidos}` : "Sin datos cargados"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 0.5 }}>Código entrevista</Typography>
                <Typography sx={{ color: "#0f172a", fontWeight: 600 }}>{codigoEntrevista || "-"}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 0.5 }}>Grupo / convivencia</Typography>
                <Typography sx={{ color: "#0f172a", fontWeight: 600 }}>{numGrupo || "-"} / {numViven || "-"}</Typography>
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        {/* Tabs de navegación */}
        <Paper
          className="entrevista-viewer__tabs-shell"
          sx={{
            mb: 3,
            borderRadius: 4,
            border: "1px solid #dbe7f3",
            overflow: "hidden",
            position: { md: "sticky" },
            top: { md: 84 },
            zIndex: 2,
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Tabs
            className="entrevista-viewer__tabs"
            value={activeSection}
            onChange={handleSectionChange}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile
            sx={{
              background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
              "& .MuiTab-root": { minHeight: 64, textTransform: "none", fontWeight: 600, color: "#475569", letterSpacing: "0.01em" },
              "& .Mui-selected": { color: "#0d6aee" },
              "& .MuiTabs-indicator": { height: 3, borderRadius: 999 },
            }}
          >
            <Tab value="datos" label="Datos del paciente" />
            <Tab value="historial" label="Historial y sistemas" />
            <Tab value="medicacion" label="Medicación y seguimiento" />
            <Tab value="final" label="Entrevista final" />
          </Tabs>
        </Paper>

        {/* Contenido: vista o edición */}
        {isEditing ? (
          <EntrevistaEdit state={state} sectionRefs={sectionRefs} sectionCardSx={sectionCardSx} />
        ) : (
          <EntrevistaView state={state} sectionRefs={sectionRefs} sectionCardSx={sectionCardSx} />
        )}

        {/* Barra de acciones */}
        <Paper
          className="entrevista-viewer__actions"
          sx={{
            p: 2,
            borderRadius: 4,
            border: "1px solid #dbe7f3",
            backgroundColor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end" useFlexGap flexWrap="wrap">
            {isEditing ? (
              <>
                <Button variant="outlined" onClick={handleCancel} sx={{ borderRadius: 2.5 }}>Cancelar</Button>
                <Button variant="contained" color="primary" onClick={handleSave} sx={{ borderRadius: 2.5 }}>Guardar cambios</Button>
                {isAdmin && (
                  <PermissionGate permission="pruebas:delete">
                    <Button variant="contained" color="error" onClick={handleDelete} sx={{ borderRadius: 2.5 }}>Eliminar</Button>
                  </PermissionGate>
                )}
              </>
            ) : (
              <>
                <Button variant="outlined" color="primary" onClick={handleEdit} sx={{ borderRadius: 2.5 }}>Editar</Button>
                <Button variant="contained" color="secondary" onClick={exportPDF} sx={{ borderRadius: 2.5 }}>Exportar PDF</Button>
                {isAdmin && (
                  <PermissionGate permission="pruebas:delete">
                    <Button variant="contained" color="error" onClick={handleDelete} sx={{ borderRadius: 2.5 }}>Eliminar</Button>
                  </PermissionGate>
                )}
              </>
            )}
          </Stack>
        </Paper>

        <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
          <Alert severity="success" onClose={() => setOpenGood(false)}>¡Informe actualizado!</Alert>
        </Snackbar>
        <Snackbar open={openBad} autoHideDuration={4000} onClose={() => setOpenBad(false)}>
          <Alert severity="error" onClose={() => setOpenBad(false)}>Error al guardar. Inténtalo de nuevo.</Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default EntrevistaViewer;
